// ============================================
// TimeAwareFollowupEngine.js - UPGRADED
// Now uses LLM for intelligent event detection (NO keywords)
// ============================================

export class TimeAwareFollowupEngine {
  constructor(azureConfig) {
    if (!azureConfig) {
      throw new Error('Azure config required');
    }
    this.endpoint = azureConfig.endpoint;
    this.deployment = azureConfig.deployment;
    this.apiKey = azureConfig.apiKey;
  }

  /**
   * Use LLM to analyze memory for time-sensitive events
   * UPGRADED: Pure AI understanding instead of keywords
   */
  async extractEventTime(memory) {
    if (!memory) return null;

    try {
      const response = await fetch(
        `${this.endpoint}/openai/deployments/${this.deployment}/chat/completions?api-version=2024-02-15-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': this.apiKey
          },
          body: JSON.stringify({
            messages: [
              {
                role: 'system',
                content: `Analyze this memory to detect if it contains a time-sensitive event.

Return ONLY valid JSON (no markdown):
{
  "hasEvent": true or false,
  "eventDescription": "what is happening",
  "estimatedTiming": "within_hours" | "within_day" | "within_week" | "past_hours" | "past_day" | "past_week" | "unknown",
  "followUpWindow": "immediately" | "hours_after" | "day_after" | "few_days_after" | "not_applicable",
  "followUpType": "how_did_it_go" | "how_are_you_feeling" | "are_you_ready" | "general_check_in" | "none",
  "confidence": 0.0 to 1.0
}

Understand context naturally - "moving tomorrow", "exam Friday", "started new job today" are events. "Love pizza" is not.`
              },
              {
                role: 'user',
                content: memory.memory || memory
              }
            ],
            temperature: 0.3,
            max_tokens: 250
          })
        }
      );

      if (!response.ok) return null;

      const data = await response.json();
      let content = data.choices[0].message.content.trim();
      content = content.replace(/```json\n?|\n?```/g, '');
      
      const analysis = JSON.parse(content);
      
      if (analysis.hasEvent) {
        console.log('🎯 Event detected:', {
          description: analysis.eventDescription.substring(0, 40),
          timing: analysis.estimatedTiming,
          confidence: analysis.confidence
        });
      }

      return analysis;

    } catch (error) {
      console.error('❌ Event extraction failed:', error);
      return null;
    }
  }

  /**
   * Calculate if NOW is the right time to follow up
   * UPGRADED: Uses memory timestamp vs current time
   */
  calculateEventTime(memory) {
    if (!memory || !memory.savedAt) return null;

    try {
      const savedTime = memory.savedAt?.toDate ? memory.savedAt.toDate() : new Date(memory.savedAt);
      const now = new Date();
      const hoursElapsed = (now - savedTime) / (1000 * 60 * 60);
      
      console.log('⏱️ Time calculation:', {
        hoursElapsed: hoursElapsed.toFixed(1),
        memory: memory.memory?.substring(0, 30)
      });

      return {
        hoursElapsed,
        savedTime,
        now
      };

    } catch (error) {
      console.error('❌ Time calculation error:', error);
      return null;
    }
  }

  /**
   * Check if event has happened and timing is right for follow-up
   * UPGRADED: Uses LLM analysis + timing window
   */
  async hasEventHappened(memory) {
    if (!memory) return false;

    const timeCalc = this.calculateEventTime(memory);
    const eventAnalysis = await this.extractEventTime(memory);

    if (!timeCalc || !eventAnalysis || !eventAnalysis.hasEvent) {
      return false;
    }

    // Match hours elapsed to follow-up window
    const { hoursElapsed } = timeCalc;
    const { followUpWindow } = eventAnalysis;

    let shouldFollowUp = false;

    if (followUpWindow === 'immediately' && hoursElapsed >= 0.5 && hoursElapsed <= 2) {
      shouldFollowUp = true;
    } else if (followUpWindow === 'hours_after' && hoursElapsed >= 1 && hoursElapsed <= 8) {
      shouldFollowUp = true;
    } else if (followUpWindow === 'day_after' && hoursElapsed >= 18 && hoursElapsed <= 30) {
      shouldFollowUp = true;
    } else if (followUpWindow === 'few_days_after' && hoursElapsed >= 48 && hoursElapsed <= 120) {
      shouldFollowUp = true;
    }

    console.log('✅ Event happened check:', shouldFollowUp);
    return shouldFollowUp;
  }

  /**
   * Get time until event
   * UPGRADED: Returns LLM analysis data
   */
  getTimeUntilEvent(memory) {
    if (!memory) return null;

    const timeCalc = this.calculateEventTime(memory);
    if (!timeCalc) return null;

    return {
      happened: true, // If we're checking this, event likely happened
      hoursAgo: Math.floor(timeCalc.hoursElapsed),
      timeCalc
    };
  }

  /**
   * Generate natural follow-up message
   * UPGRADED: Uses LLM to create context-aware responses
   */
  async generateTimeAwareFollowUp(userName, userMessage, relevantMemories, lastMessageTime, lastChatTime) {
    try {
      if (!relevantMemories || relevantMemories.length === 0) {
        console.log('📭 No memories for follow-up');
        return null;
      }

      console.log('🔍 Scanning for follow-up opportunities...');

      // Check each memory for follow-up potential
      const opportunities = [];

      for (const memory of relevantMemories.slice(0, 10)) {
        const analysis = await this.extractEventTime(memory);
        
        if (analysis && analysis.hasEvent) {
          const shouldFollow = await this.hasEventHappened(memory);
          
          if (shouldFollow) {
            opportunities.push({
              memory,
              analysis,
              priority: analysis.confidence
            });
          }
        }
      }

      if (opportunities.length === 0) {
        console.log('⏭️ No follow-up opportunities now');
        return null;
      }

      // Pick best opportunity
      const best = opportunities.sort((a, b) => b.priority - a.priority)[0];

      console.log('✅ Best follow-up found:', best.analysis.eventDescription.substring(0, 40));

      // Generate natural message
      const response = await fetch(
        `${this.endpoint}/openai/deployments/${this.deployment}/chat/completions?api-version=2024-02-15-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': this.apiKey
          },
          body: JSON.stringify({
            messages: [
              {
                role: 'system',
                content: `You're a caring friend following up naturally about this:

Event: "${best.memory.memory}"
Type: ${best.analysis.followUpType}
They just said: "${userMessage}"

Generate ONE natural follow-up (1 sentence max). Sound like a real friend, not AI.
NO "I remember..." phrases. Be specific and warm.
- Never use em dashes (—), use commas or periods instead. Speak naturally.

Examples:
✅ "How'd the moving go?"
✅ "That exam must be done - how'd it go?"
✅ "First day at the new job - how's it treating you?"

Respond with ONLY the message.`
              },
              {
                role: 'user',
                content: 'Generate follow-up'
              }
            ],
            temperature: 0.85,
            max_tokens: 60
          })
        }
      );

      if (!response.ok) {
        console.error('❌ Generation error:', response.status);
        return null;
      }

      const data = await response.json();
      const followUp = data.choices[0].message.content.trim();

      console.log('✅ Generated follow-up:', followUp);
      return followUp;

    } catch (error) {
      console.error('❌ Follow-up generation error:', error);
      return null;
    }
  }

  /**
   * Format time info for display
   */
  formatTimeElapsed(timeInfo) {
    if (!timeInfo) return 'unknown time';
    
    const { hours, days } = timeInfo;
    
    if (hours < 1) return 'less than an hour ago';
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
    
    return `${Math.floor(days / 7)} week${Math.floor(days / 7) !== 1 ? 's' : ''} ago`;
  }

  /**
   * Format time until event
   */
  formatTimeUntil(minutes, hours, days) {
    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''} and ${hours % 24} hour${(hours % 24) !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} and ${minutes % 60} minute${(minutes % 60) !== 1 ? 's' : ''}`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Determine if should ask about event
   */
  shouldAskAboutEvent(memory) {
    if (!memory) return false;
    return memory.isTimeSensitive === true;
  }

  /**
   * Get time since last message
   */
  getTimeSinceLastMessage(lastMessageTimestamp) {
    if (!lastMessageTimestamp) return null;

    const now = new Date();
    const lastTime = new Date(lastMessageTimestamp);
    const diffMs = now - lastTime;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    return {
      ms: diffMs,
      hours: diffHours,
      days: diffDays,
      category: this.categorizeTime(diffMs)
    };
  }

  /**
   * Categorize time elapsed
   */
  categorizeTime(diffMs) {
    const hours = diffMs / (1000 * 60 * 60);
    const days = hours / 24;

    if (hours < 2) return 'sameSession';
    if (hours < 8) return 'sameDay';
    if (days < 1.5) return 'nextDay';
    if (days < 7) return 'thisWeek';
    if (days < 30) return 'thisMonth';
    return 'longTime';
  }
}

export default TimeAwareFollowupEngine;